import "./drag-drop.less"
import { hasText } from "utils/helpers"
import {
  DropZonePayload,
  PlayAnswerPile,
  PlayDragDropAnswer,
  PlayDragDropState,
  PlayDropZone,
  PlayHubState,
  StartDraggingFromAnswersPayload,
  StartDraggingFromZonePayload,
} from "../play-model"
import { DragDropContainer } from "../components/dragdrop/drag-drop-container"
import { DropZone } from "../components/dragdrop/drop-zone"
import { DragDropHandler } from "../components/dragdrop/drag-drop-context"
import { DragTarget } from "../components/dragdrop/drag-target"
import { usePlayState } from "play/hooks/use-play-state"
import { useMessages } from "play/language/language"
import { useEffect, useState } from "react"

interface DragDropProps {
  dragDrop: PlayDragDropState
  finished: boolean
  showCorrect: boolean
  isTeacher: boolean

  onDragOver(args: { targetId: string; answerId: string }): void

  onDrop(args: { targetId: string; answerId: string }): void

  onDragStartFromZone(args: {
    answerId: string
    targetIdx: number
    zoneId: string
  }): void

  onDragStart(args: { answerId: string; zoneIndex: number }): void

  onDragZoneExit(args: { targetId: string; answerId: string }): void

  onDragEnd(): void
}

export default function Connect() {
  const { dragDrop, finished, showCorrectAnswers, isTeacherUrl, modifyState } =
    usePlayState()

  function startDraggingFromZone(payload: StartDraggingFromZonePayload) {
    modifyState((state) => {
      const { dragDrop } = state

      // console.log('startDraggingFromZoneReducer a=%s z=%s', payload.answerId, payload.zoneId)

      // dragDrop.draggedAnswerSourceIndex = -1;
      // dragDrop.draggedFromZoneId = payload.zoneId;
      // dragDrop.draggedFromZoneTargetIndex = payload.targetIdx;

      const zone = dragDrop.dropZones.find(
        (x) => x.categoryId === payload.zoneId
      )
      if (zone) {
        zone.targets[payload.targetIdx].isDraggedFrom = true
      }
    })
  }

  function dropZoneExit(payload: DropZonePayload) {
    modifyState((state) => {
      const { dragDrop } = state

      // console.log('dropZoneExitReducer', payload)

      const zone = dragDrop.dropZones.find(
        (x) => x.categoryId === payload.targetId
      )
      if (zone) {
        zone.isDraggedOver = false
      }
    })
  }

  function stopDraggingAnswer() {
    modifyState((state) => {
      const { dragDrop } = state

      // console.log('stopDraggingAnswerReducer')

      dragDrop.dropZones.forEach((zone) => {
        zone.isDraggedOver = false
        zone.targets.forEach((target) => {
          target.isDraggedFrom = false
        })
      })

      // dragDrop.draggedAnswerSourceIndex = -1;
      // dragDrop.draggedFromZoneId = null;
      // dragDrop.draggedFromZoneTargetIndex = -1;
    })
  }

  function draggingOverZone(payload: DropZonePayload) {
    modifyState((state) => {
      const { dragDrop } = state

      // console.log('draggingOverZoneReducer', payload)

      dragDrop.dropZones.forEach((zone) => {
        zone.isDraggedOver = zone.categoryId === payload.targetId
      })
    })
  }

  function dropOverZone(payload: DropZonePayload) {
    modifyState((state) => {
      const { dragDrop } = state

      // console.log('dropOverZoneReducer t=%s', action.payload.targetId)

      const zone = dragDrop.dropZones.find(
        (x) => x.categoryId === payload.targetId
      )

      if (!zone) {
        console.warn("Cannot find drop zone with id %s!", payload.targetId)
        return
      }

      const dragSource = computeDragSource(payload.answerId, dragDrop)

      let answer: PlayDragDropAnswer

      if (dragSource.draggedAnswerSourceIndex !== -1) {
        const source = dragDrop.answerPiles[dragSource.draggedAnswerSourceIndex]
        const answerIdx = source.answers.findIndex(
          (x) => x.id === payload.answerId
        )

        if (answerIdx === -1) {
          console.warn(
            "Failed to find answer %s from source pile %d!",
            payload.answerId,
            dragSource.draggedAnswerSourceIndex
          )
          return
        }

        answer = source.answers.splice(answerIdx, 1)[0]
      } else if (
        dragSource.draggedFromZoneId !== null &&
        dragSource.draggedFromZoneTargetIndex !== -1
      ) {
        const sourceZone = dragDrop.dropZones.find(
          (x) => x.categoryId === dragSource.draggedFromZoneId
        )
        if (!sourceZone) {
          console.warn(
            "Failed to find source target: %s",
            dragSource.draggedFromZoneId
          )
          return
        }

        const sourceTarget =
          sourceZone.targets[dragSource.draggedFromZoneTargetIndex]

        const answerIdx = sourceTarget.answers.findIndex(
          (x) => x.id === payload.answerId
        )

        if (answerIdx === -1) {
          console.warn(
            "Failed to find answer %s from source pile %d!",
            payload.answerId,
            dragSource.draggedAnswerSourceIndex
          )
          return
        }

        answer = sourceTarget.answers.splice(answerIdx, 1)[0]
      } else {
        console.warn("Drag source is neither answers or a drop zone")
        return
      }

      // find the best target
      const sortedTargets = zone.targets
        .map((x, idx) => ({
          ...x,
          ordinal: idx,
        }))
        .sort(
          (pileA, pileB) =>
            // sort ascending by answers and original ordinal
            (pileA.answers.length - pileB.answers.length) * 1_000 +
            (pileA.ordinal - pileB.ordinal)
        )

      const target = sortedTargets[0]
      target.answers.push(answer)

      // dragDrop.draggedAnswerSourceIndex = -1;
      // dragDrop.draggedFromZoneId = null;
      // dragDrop.draggedFromZoneTargetIndex = -1;

      let areAllPilesEmpty = true
      let countPiles = dragDrop.answerPiles.length
      for (let pileIndex = 0; pileIndex < countPiles; pileIndex++) {
        const pile = dragDrop.answerPiles[pileIndex]
        const hasItems = pile.answers.length > 0
        if (hasItems) {
          areAllPilesEmpty = false
          break
        }
      }

      zone.isDraggedOver = false
      state.nextScreenButtonIsActive = areAllPilesEmpty
    })
  }

  function startDraggingFromAnswers(payload: StartDraggingFromAnswersPayload) {
    modifyState((state) => {
      const { dragDrop } = state

      // console.log('startDraggingFromAnswersReducer a=%s z=%s', payload.answerId, payload.zoneIndex)

      // dragDrop.draggedAnswerSourceIndex = payload.zoneIndex;
      // dragDrop.draggedFromZoneId = null;
      // dragDrop.draggedFromZoneTargetIndex = -1;

      dragDrop.answerPiles[payload.zoneIndex].isDraggedFrom = true
    })
  }

  return (
    <DragDropScreenView
      dragDrop={dragDrop}
      isTeacher={isTeacherUrl}
      finished={finished}
      showCorrect={showCorrectAnswers}
      onDragEnd={stopDraggingAnswer}
      onDragOver={draggingOverZone}
      onDrop={dropOverZone}
      onDragZoneExit={dropZoneExit}
      onDragStart={startDraggingFromAnswers}
      onDragStartFromZone={startDraggingFromZone}
    />
  )
}

export function DragDropScreenView(props: DragDropProps) {
  const msg = useMessages()
  const { dragDrop, finished, showCorrect, isTeacher } = props
  const { dropZones, answerPiles } = dragDrop
  const {
    onDragOver,
    onDrop,
    onDragStartFromZone,
    onDragStart,
    onDragZoneExit,
    onDragEnd,
  } = props

  function handleDragOver(zone: PlayDropZone, id: string) {
    if (isTeacher) return

    onDragOver({ targetId: zone.categoryId, answerId: id })
  }

  function handleDrop(zone: PlayDropZone, draggedAnswerId: string) {
    if (isTeacher) return

    onDrop({ targetId: zone.categoryId, answerId: draggedAnswerId })
  }

  function handleDragStartFromZone(
    zone: PlayDropZone,
    targetIdx: number,
    answer: PlayDragDropAnswer
  ) {
    if (isTeacher) {
      return
    }

    onDragStartFromZone({
      answerId: answer.id,
      targetIdx: targetIdx,
      zoneId: zone.categoryId,
    })
  }

  function handleDragStart(
    answer: PlayDragDropAnswer,
    answerPileIndex: number,
    answerId: string
  ) {
    if (isTeacher) {
      return
    }

    onDragStart({
      answerId: answerId,
      zoneIndex: answerPileIndex,
    })
  }

  function handleDragZoneExit(zone: PlayDropZone, draggedAnswerId: string) {
    if (isTeacher) return

    onDragZoneExit({ targetId: zone.categoryId, answerId: draggedAnswerId })
  }

  function handleDragEnd(answer: PlayDragDropAnswer, answerPileIndex: number) {
    if (isTeacher) return

    onDragEnd()
  }

  return (
    <DragDropContainer className="drag-drop-screen play-screen">
      <div className={`drop-zones total-${dropZones.length}`}>
        {dropZones.map((zone) => (
          <Zone
            key={zone.categoryId}
            zone={zone}
            showCorrect={showCorrect}
            finished={finished}
            isTeacher={isTeacher}
            onDragOver={(e) => handleDragOver(zone, e)}
            onDrop={(e) => handleDrop(zone, e)}
            onDragExit={(e) => handleDragZoneExit(zone, e)}
            onAnswerDragStart={(targetIdx, answer) =>
              handleDragStartFromZone(zone, targetIdx, answer)
            }
          />
        ))}
      </div>
      {answerPiles.length > 0 && (
        <div className="answers" aria-label={msg.answers}>
          {answerPiles.map((pile, pileIndex) => (
            <div
              key={pileIndex}
              className={`pile ${pile.answers.length == 0 ? "empty" : ""}`}
              role="list"
            >
              {pile.answers.map((answer, answerIndex) => {
                if (answerIndex === pile.answers.length - 1) {
                  return (
                    <Answer
                      key={answer.id}
                      finished={finished}
                      onDragStart={(e) => handleDragStart(answer, pileIndex, e)}
                      answer={answer}
                      isTeacher={isTeacher}
                    />
                  )
                } else {
                  return (
                    <Answer
                      key={answer.id}
                      answer={answer}
                      isTeacher={isTeacher}
                      finished={finished}
                    />
                  )
                }
              })}
            </div>
          ))}
        </div>
      )}
    </DragDropContainer>
  )
}

interface TargetProps {
  target: PlayAnswerPile
  categoryId: string
  showCorrect: boolean
  onAnswerDragStart: (answer: PlayDragDropAnswer) => void
  isTeacher: boolean
  finished: boolean
}

function Target(props: TargetProps) {
  const {
    target,
    onAnswerDragStart,
    showCorrect,
    categoryId,
    isTeacher,
    finished,
  } = props

  const answers = [...target.answers].reverse()

  return (
    <div className={`target count-${answers.length}`}>
      {answers.map((answer, answerIndex) => {
        return (
          <Answer
            key={answer.id}
            answer={answer}
            showCorrect={showCorrect}
            categoryId={categoryId}
            onDragStart={() => onAnswerDragStart(answer)}
            isTeacher={isTeacher}
            finished={finished}
          />
        )
      })}
    </div>
  )
}

interface ZoneProps {
  zone: PlayDropZone
  showCorrect: boolean
  isTeacher: boolean
  finished: boolean
  onDragOver: DragDropHandler
  onDragExit: DragDropHandler
  onDrop: DragDropHandler
  onAnswerDragStart: (targetIdx: number, answer: PlayDragDropAnswer) => void
}

function Zone(props: ZoneProps) {
  const msg = useMessages()
  const {
    zone,
    onDragOver,
    onDrop,
    onDragExit,
    onAnswerDragStart,
    showCorrect,
    isTeacher,
    finished,
  } = props

  let className = "zone"
  if (zone.isDraggedOver) {
    className += " selected"
  }

  return (
    <DropZone
      zoneId={zone.categoryId}
      className={className}
      onDragOver={onDragOver}
      onDragExit={onDragExit}
      onDrop={onDrop}
    >
      <div className="targets">
        {zone.targets.map((target, targetIndex) => (
          <Target
            key={targetIndex}
            target={target}
            showCorrect={showCorrect}
            categoryId={zone.categoryId}
            isTeacher={isTeacher}
            finished={finished}
            onAnswerDragStart={(answer) =>
              onAnswerDragStart(targetIndex, answer)
            }
          />
        ))}
      </div>
      {hasText(zone.title) && (
        <div className="title-container">
          <div className="title" aria-label={msg.dragDropCategoryLabel}>
            {zone.title}
          </div>
        </div>
      )}
    </DropZone>
  )
}

interface AnswerProps {
  answer: PlayDragDropAnswer
  onDragStart?: DragDropHandler
  onDragEnd?: DragDropHandler
  showCorrect?: boolean
  categoryId?: string
  isTeacher: boolean
  finished: boolean
}

function Answer(props: AnswerProps) {
  const {
    answer,
    finished,
    onDragStart,
    onDragEnd,
    showCorrect,
    categoryId,
    isTeacher,
  } = props
  const msg = useMessages()
  const draggable = !isTeacher && !!onDragStart && !finished
  const [state, setState] = useState({
    width: 0,
    height: 0,
  })
  // console.log('isTeacher', isTeacher, 'hasOnDragStart', !!onDragStart, 'showCorrect', showCorrect)

  const isText =
    answer.text !== null &&
    typeof answer.text === "string" &&
    answer.text.length > 0
  // console.log({isText, text: answer.text, url: answer.imageUrl})

  useEffect(() => {
    if (!hasText(answer.imageUrl)) return

    function imageLoaded() {
      const MAX_WIDTH = 90
      const MAX_HEIGHT = 90
      let width = img.naturalWidth
      let height = img.naturalHeight

      let r = width / height

      if (width > MAX_WIDTH) {
        width = MAX_WIDTH
        height = MAX_WIDTH / r
      }
      if (height > MAX_HEIGHT) {
        height = MAX_HEIGHT
        width = MAX_HEIGHT * r
      }

      setState({
        width: width,
        height: height,
      })
    }

    const img = new Image()
    img.src = answer.imageUrl
    img.addEventListener("load", imageLoaded)

    return () => {
      img.removeEventListener("load", imageLoaded)
    }
  }, [answer.imageUrl])

  let answerClass = "answer"
  if (!draggable) {
    answerClass += " behind"
  }

  if (isText) {
    answerClass += " is-text"
  }

  let correctClass = ""

  if (showCorrect === true) {
    let isCorrect: boolean
    if (typeof answer.isCorrect === "boolean") {
      isCorrect = answer.isCorrect
    } else {
      isCorrect = answer.categoryId === categoryId
    }
    correctClass = isCorrect ? "correct" : "incorrect"
  }

  if (isText) {
    const className = `text ${correctClass} user-select-none`

    return (
      <DragTarget
        className={answerClass}
        draggable={draggable}
        dragTargetId={answer.id}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        role="listitem"
      >
        <div
          className={className}
          draggable={false}
          id={answer.id}
          aria-label={msg.textAnswerLabel}
        >
          {answer.text}
        </div>
      </DragTarget>
    )
  } else {
    const className = `img ${answer.imageType} ${correctClass} user-select-none`
    answerClass += " rounded shadow-sm"

    return (
      <DragTarget
        className={answerClass}
        draggable={draggable}
        dragTargetId={answer.id}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        role="listitem"
        aria-label={msg.dragDropImageAnswerAlt}
        style={{
          backgroundImage: `url(${answer.imageUrl})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          width: state.width,
          height: state.height,
        }}
      >
        {/*<img*/}
        {/*  className={className}*/}
        {/*  draggable={false}*/}
        {/*  src={answer.imageUrl}*/}
        {/*  alt={msg.dragDropImageAnswerAlt}*/}
        {/*/>*/}
      </DragTarget>
    )
  }
}

function computeDragSource(answerId: string, dnd: PlayHubState["dragDrop"]) {
  let draggedAnswerSourceIndex = -1
  let draggedFromZoneId: string | null = null
  let draggedFromZoneTargetIndex = -1

  let done = false

  for (let pileIdx in dnd.answerPiles) {
    const pile = dnd.answerPiles[pileIdx]

    for (let answerIdx in pile.answers) {
      const answer = pile.answers[answerIdx]

      if (answer.id === answerId) {
        draggedAnswerSourceIndex = Number(pileIdx)
        done = true
        break
      }
    }

    if (done) {
      break
    }
  }

  if (!done) {
    for (let zoneIdx in dnd.dropZones) {
      const zone = dnd.dropZones[zoneIdx]

      for (let targetIdx in zone.targets) {
        const target = zone.targets[targetIdx]

        for (let answerIdx in target.answers) {
          const answer = target.answers[answerIdx]

          if (answer.id === answerId) {
            draggedFromZoneId = zone.categoryId
            draggedFromZoneTargetIndex = Number(targetIdx)

            done = true
            break
          }
        }

        if (done) {
          break
        }
      }

      if (done) {
        break
      }
    }
  }

  return {
    draggedAnswerSourceIndex,
    draggedFromZoneId,
    draggedFromZoneTargetIndex,
  }
}
